import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { LoadingSpinner, Modal, SearchField } from "@mapmycustomers/ui";

import {
  clickOnGlobalSearchItem,
  fetchUserRecents,
  hideGlobalSearch,
  performSearch,
  resetActiveQuery,
} from "@app/store/globalSearch/actions";
import {
  getGlobalSearchResults,
  getUserRecents,
  isUserRecentsLoading,
} from "@app/store/globalSearch/selectors";
import { getLeadGenPlan, isCurrentUserOwner, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useInterval from "@app/util/hook/useInterval";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import localSettings from "../../config/LocalSettings";
import GlobalSearchItemType from "../../enum/GlobalSearchItemType";
import Path from "../../enum/Path";
import GlobalSearchItem from "../../types/globalSearch/GlobalSearchItem";

import BottomInfo from "./components/BottomInfo";
import Item from "./components/Item";
import NoResults from "./components/NoResults";
import TypeSelector from "./components/TypeSelector";
import GlobalSearchFilter, { getGlobalSearchFilterValue } from "./enum/GlobalSearchFilter";
import styles from "./GlobalSearch.module.scss";

const GlobalSearchModal = ({
  isOwner,
  isReadOnlyMember,
  leadGenPlan,
  loading,
  onClickOnGlobalSearchItem,
  onFetchUserRecents,
  onHideGlobalSearch,
  onPerformSearch,
  onResetActiveQuery,
  searchResults,
  userRecents,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();
  const analytics = useExtendedAnalytics("Universal Search", useAnalytics());

  useEffect(() => {
    analytics.clicked([]);
  }, [analytics]);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [direction, setDirection] = useState<-1 | 0 | 1>(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<GlobalSearchFilter>(GlobalSearchFilter.ALL);
  const handlePerformSearch = useDynamicCallback(
    (query: string, overrideFilter?: GlobalSearchFilter) => {
      if (query.trim().length >= 3) {
        onPerformSearch({
          entityTypes: getGlobalSearchFilterValue(overrideFilter ?? filter),
          query,
        });
      } else {
        onResetActiveQuery();
      }
    }
  );

  useChangeTracking(() => {
    analytics.clicked(["Filter"], { filter });
  }, [analytics, filter]);

  const [query, setQuery, resetQuery] = useStateWithDebouncedListener(
    [handlePerformSearch, 500],
    ""
  );

  const searchActivated = query.length >= 3;

  useEffect(() => {
    if (query.length) {
      localSettings.setGlobalSearchQuery(query);
    }
  }, [query]);

  const handleSetFilter = useCallback(
    (filter: GlobalSearchFilter) => {
      setFilter(filter);
      if (searchActivated) {
        handlePerformSearch(query, filter);
      }
    },
    [handlePerformSearch, query, setFilter, searchActivated]
  );

  useEffect(() => {
    searchRef.current?.focus();
    onFetchUserRecents();
  }, [onFetchUserRecents, searchRef]);

  const handleSelect = useCallback(
    (item: GlobalSearchItem) => {
      if (GlobalSearchItemType.LEAD_FINDER === item.type) {
        analytics.clicked(["Find new leads based on search"]);
        if (leadGenPlan === LeadFinderSubscriptionPlan.NONE) {
          showLeadFinderUpsell(leadGenPlan, intl, isOwner);
        } else {
          history.push(`${Path.MAP}?findLeads&findLeadsQuery=${query}`);
          onHideGlobalSearch();
        }
      } else if (
        [GlobalSearchItemType.LOCAL_QUERY, GlobalSearchItemType.QUERY].includes(item.type)
      ) {
        analytics.clicked([" Clicked most recent search"]);
        handlePerformSearch(item.title);
        resetQuery(item.title);
      } else if (item.entity && item.entityType) {
        analytics.clicked(["Clicked row"]);
        onClickOnGlobalSearchItem({
          entity: item.entity,
          entityId: item.entity.id,
          entityType: item.entityType,
        });
        onHideGlobalSearch();
      }
    },
    [
      analytics,
      leadGenPlan,
      intl,
      isOwner,
      history,
      query,
      onHideGlobalSearch,
      handlePerformSearch,
      resetQuery,
      onClickOnGlobalSearchItem,
    ]
  );

  const noResults = (searchActivated ? searchResults : userRecents).length === 0;

  const items = useMemo(() => {
    const result = searchActivated
      ? [
          ...(filter === GlobalSearchFilter.ALL && !isReadOnlyMember
            ? [
                {
                  entity: undefined,
                  entityType: undefined,
                  title: "",
                  type: GlobalSearchItemType.LEAD_FINDER,
                },
              ]
            : []),
          ...searchResults,
        ]
      : userRecents;
    const newIndex = result.findIndex(({ type }) => type !== GlobalSearchItemType.LEAD_FINDER);
    setSelectedIndex(newIndex === -1 ? 0 : newIndex);
    return result;
  }, [filter, isReadOnlyMember, searchActivated, searchResults, userRecents]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === "ArrowUp" || e.code === "ArrowDown") {
        setDirection(e.code === "ArrowUp" ? -1 : 1);
      }
    },
    [setDirection]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === "ArrowUp" || e.code === "ArrowDown") {
        setDirection(0);
      } else if (e.key === "Enter") {
        if (selectedIndex > -1) {
          handleSelect(items[selectedIndex]);
        }
      } else if (e.code === "Escape") {
        if (query.length) {
          setQuery(""); // first escape clears query
        } else {
          onHideGlobalSearch(); // second escape closes modal
        }
      }
    },
    [handleSelect, onHideGlobalSearch, query, selectedIndex, setQuery, setDirection, items]
  );

  const handleCancel = useCallback(() => {
    onHideGlobalSearch();
  }, [onHideGlobalSearch]);

  const changeItemIndex = useCallback(() => {
    setSelectedIndex((index) => {
      const newIndex = (index + direction + items.length) % items.length;
      const element = document.querySelector(`[data-global-search-index='${newIndex}'`);
      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "center" });
      }
      return newIndex;
    });
  }, [direction, items, setSelectedIndex]);

  useInterval(changeItemIndex, direction !== 0, 100, true, 400);

  useEffect(() => setSelectedIndex(-1), [query, setSelectedIndex]);

  useEffect(() => {
    document.body.addEventListener("keyup", handleKeyUp);
    document.body.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.removeEventListener("keyup", handleKeyUp);
      document.body.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, handleKeyUp]);

  return (
    <Modal
      centered
      className={styles.container}
      closeIcon={null}
      footer={null}
      keyboard={false}
      onCancel={handleCancel}
      open
      width="clamp(300px, 60vw, 750px)"
    >
      <SearchField
        allowClear
        className={styles.search}
        onChange={setQuery}
        placeholder={intl.formatMessage({
          id: "component.globalSearch.search.placeholder",
          defaultMessage: "Type at least 3 characters to begin search",
          description: "Search text on global search modal",
        })}
        ref={searchRef}
        size="large"
        value={query}
      />
      <TypeSelector onSelect={handleSetFilter} value={filter} />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className={styles.contentWrapper}>
          {!searchActivated && (
            <div className={styles.recentText}>
              <div>
                {intl.formatMessage({
                  id: "component.globalSearch.search.recent",
                  defaultMessage: "Recent",
                  description: "recent text on global search modal",
                })}
              </div>
            </div>
          )}

          {items.length ? (
            <div className={cn({ [styles.listContainer]: !noResults })}>
              {items.map((userRecent, i) => (
                <Item
                  index={i}
                  isSelected={selectedIndex === i}
                  item={userRecent}
                  key={i}
                  onClick={handleSelect}
                  onSetSelected={setSelectedIndex}
                />
              ))}
            </div>
          ) : null}
          {noResults ? <NoResults /> : <BottomInfo />}
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  leadGenPlan: getLeadGenPlan(state),
  loading: isUserRecentsLoading(state),
  searchResults: getGlobalSearchResults(state),
  userRecents: getUserRecents(state),
});

const mapDispatchToProps = {
  onClickOnGlobalSearchItem: clickOnGlobalSearchItem,
  onFetchUserRecents: fetchUserRecents.request,
  onHideGlobalSearch: hideGlobalSearch,
  onPerformSearch: performSearch.request,
  onResetActiveQuery: resetActiveQuery,
};

export default connect(mapStateToProps, mapDispatchToProps)(GlobalSearchModal);
